import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  Group,
  MultiSelect,
  NumberInput,
  Select,
  Slider,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, Form, useFieldArray, useForm } from 'react-hook-form';
import { FiMapPin, FiPlus, FiTrash2 } from 'react-icons/fi';
import MapsAutoCompleteV2 from '@components/elements/AutoCompleteAddress/MapsAutoComplete-v2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import useNotification from '@hooks/useNotification';
import DisableNotificationsConfirmModal from '@worker/components/MyJobs/modals/DisableNotificationsConfirmModal';
import {
  useWorkerOptions,
  useWorkerProfile,
} from '@worker/hooks/useWorkerData';
import { useCompaniesSuggestions } from '@worker/hooks/useCompaniesSuggestions';
import { saveJobRecommendationsPreferences } from '@worker/services/MyJobs/api';
import { useWorkerStore } from '@worker/stores';
import {
  JobRecommendationsPreferencesPayload,
  JobsConfigFormValues,
  NotificationChannels,
  NotificationFrequency,
} from '@worker/types/myJobs.type';
import {
  DAYS_30,
  MIN_SEARCH_CHARS,
  SEARCH_DEBOUNCE_MS,
} from '@constants/various';
import QueryKeys from '@constants/queryKeys';

interface JobsConfigFormProps {
  defaultValues?: Partial<JobsConfigFormValues>;
  onCancel?: () => void;
  onSubmit?: () => void;
  onClose?: () => void; // Called when modal is closed (X button, ESC, click outside)
  handleGetInNetwork?: () => void;
  onAutoSaveDefaults?: (saveFn: () => void) => void; // Callback to expose the save defaults function
}

const DEFAULT_ADDRESS = '2045 W Jackson Blvd Chicago, Illinois, 60612';
const MAX_PREFERRED_ADDRESSES = 3;
const MIN_DISTANCE_MILES = 5;
const MAX_DISTANCE_MILES = 100;
const DEFAULT_PREFERRED_DISTANCE = 35;

export default function JobsConfigForm({
  defaultValues,
  onCancel,
  onSubmit,
  onClose,
  handleGetInNetwork = () => undefined,
  onAutoSaveDefaults,
}: JobsConfigFormProps) {
  const queryClient = useQueryClient();
  const { data: workerProfileAllowedOptions } = useWorkerOptions();
  const { data: workerProfile } = useWorkerProfile();
  const {
    showSuccessNotification,
    showWarningNotification,
    showErrorNotification,
  } = useNotification();

  const existingPreferences = useWorkerStore(
    (state) => state.workerProfile.jobPreferences
  );

  // Check if user is in-network (premium)
  const isInNetwork = useWorkerStore((state) => state.isInNetwork());
  const enableSuggestedWorkers =
    import.meta.env.VITE_ENABLE_SUGGESTED_WORKERS === 'true';

  // Company search for exclude companies feature
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedCompanySearch] = useDebouncedValue(
    companySearch,
    SEARCH_DEBOUNCE_MS
  );

  const { data: companiesSuggestions } = useCompaniesSuggestions(
    debouncedCompanySearch
  );

  // Combine existing ignored companies with search suggestions for MultiSelect
  // This ensures selected companies show their names (not IDs) when modal reopens
  const excludeCompanyOptions = useMemo(() => {
    const existingCompanies =
      existingPreferences?.ignoredCompanies?.map((company) => ({
        label: company.name,
        value: String(company.id),
      })) ?? [];

    const newSuggestions =
      companiesSuggestions?.suggestions
        .filter(
          (company) =>
            !existingPreferences?.ignoredCompanies?.some(
              (ignored) => ignored.id === company.id
            )
        )
        .map((company) => ({
          label: company.name,
          value: String(company.id),
        })) ?? [];

    return [...existingCompanies, ...newSuggestions];
  }, [
    existingPreferences?.ignoredCompanies,
    companiesSuggestions?.suggestions,
  ]);

  const initialValues: JobsConfigFormValues = useMemo(
    () => ({
      frequency:
        existingPreferences?.notificationFrequency ||
        NotificationFrequency.DAILY,
      receiveChannels: existingPreferences?.preferredChannels || [
        NotificationChannels.EMAIL,
        NotificationChannels.PUSH_NOTIFICATION,
      ],
      jobTitles: existingPreferences?.preferredTitles
        ? existingPreferences.preferredTitles.map((jt: { id: number }) =>
            String(jt.id)
          )
        : workerProfileAllowedOptions?.jobTitles?.map((jt: { id: number }) =>
            String(jt.id)
          ) || [],
      minHourlyEnabled: existingPreferences?.minPayRateHourly != null,
      minHourlyRate: existingPreferences?.minPayRateHourly ?? undefined,
      minDailyEnabled: existingPreferences?.minPayRateDaily != null,
      minDailyRate: existingPreferences?.minPayRateDaily ?? undefined,
      currentAddress:
        workerProfile?.worker?.address?.googleAddress || DEFAULT_ADDRESS,
      distance:
        existingPreferences?.maxDistanceMiles || DEFAULT_PREFERRED_DISTANCE,
      preferredAddresses:
        existingPreferences?.preferredAddresses?.map((addr) => ({
          id: addr.id,
          address: addr.address,
          addressLatitude: addr.addressLatitude,
          addressLongitude: addr.addressLongitude,
          maxDistanceMiles: addr.maxDistanceMiles,
        })) || [],
      excludeCompany:
        existingPreferences?.ignoredCompanies?.map((company) =>
          String(company.id)
        ) || [],
      allowSuggestedAppearance: !isInNetwork
        ? false
        : (existingPreferences?.allowSuggestedAppearance ?? true),
      ...defaultValues,
    }),
    [
      defaultValues,
      existingPreferences,
      isInNetwork,
      workerProfile?.worker?.address?.googleAddress,
      workerProfileAllowedOptions?.jobTitles,
    ]
  );

  const {
    control,
    watch,
    setValue,
    formState: { isSubmitting },
    reset,
  } = useForm<JobsConfigFormValues>({ defaultValues: initialValues });

  // Additional addresses are a repeatable list (In-Network only, see MAX_PREFERRED_ADDRESSES)
  const {
    fields: preferredAddressFields,
    append: appendPreferredAddress,
    remove: removePreferredAddress,
  } = useFieldArray({ control, name: 'preferredAddresses' });

  // First-time user = setupCompletedAt is null (user hasn't saved preferences yet)
  const isFirstTimeUser = !existingPreferences?.setupCompletedAt;
  const minHourlyEnabled = watch('minHourlyEnabled');
  const minDailyEnabled = watch('minDailyEnabled');
  const currentFrequency = watch('frequency');

  // Calculate days remaining until notifications resume (if paused)
  const daysUntilResume = useMemo(() => {
    if (
      currentFrequency !== NotificationFrequency.PAUSED ||
      !existingPreferences?.notificationPausedUntil
    ) {
      return null;
    }
    const pausedUntil = new Date(existingPreferences.notificationPausedUntil);
    const now = new Date();
    const diffTime = pausedUntil.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  }, [currentFrequency, existingPreferences?.notificationPausedUntil]);

  // State for disable notifications confirmation modal
  const [showDisableModal, setShowDisableModal] = useState(false);

  /**
   * Mutation for saving job recommendation preferences
   * Waits for server confirmation before closing the modal
   */
  const savePreferencesMutation = useMutation({
    mutationFn: saveJobRecommendationsPreferences,
    onSuccess: async () => {
      showSuccessNotification(
        'Your job preferences have been saved successfully!'
      );

      // Wait for refetch so setupCompletedAt is updated in store before closing
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.WORKER_PROFILE],
      });

      onSubmit?.();
      if (onClose) {
        onClose();
      } else {
        onCancel?.();
      }
    },
    onError: () => {
      showErrorNotification(
        'Something went wrong saving your preferences. Please try again.'
      );
      onClose?.();
    },
  });

  const handleInternalSubmit = (values: JobsConfigFormValues) => {
    const preferredTitleIds = values.jobTitles.map((id) => Number(id));
    const ignoredCompanyIds = values.excludeCompany.map((id) => Number(id));

    const preferredAddresses = isInNetwork
      ? values.preferredAddresses
          .filter((addr) => addr.address.trim().length > 0)
          .map((addr) => ({
            address: addr.address,
            addressLatitude: addr.addressLatitude,
            addressLongitude: addr.addressLongitude,
            maxDistanceMiles: addr.maxDistanceMiles,
          }))
      : [];

    const payload = {
      minPayRateHourly: minHourlyEnabled ? values.minHourlyRate : null,
      minPayRateDaily: minDailyEnabled ? values.minDailyRate : null,
      notificationFrequency: values.frequency,
      preferredChannels: values.receiveChannels,
      preferredTitles: preferredTitleIds,
      ignoredCompanies: ignoredCompanyIds,
      maxDistanceMiles: values.distance || null,
      preferredAddresses,
      ...(enableSuggestedWorkers
        ? { allowSuggestedAppearance: values.allowSuggestedAppearance }
        : {}),
      notificationPausedUntil:
        values.frequency === NotificationFrequency.PAUSED
          ? new Date(Date.now() + DAYS_30).toISOString() // 30 days from now
          : null,
    };

    savePreferencesMutation.mutate(
      payload as unknown as JobRecommendationsPreferencesPayload
    );
  };

  const handleCancel = () => {
    onCancel?.();
  };

  const handleCompanySearchChange = (value: string) => {
    setCompanySearch(value);
  };

  const handleSelectPreferredPlace = (
    index: number,
    place: google.maps.places.PlaceResult | null
  ) => {
    if (!place?.geometry?.location) {
      return;
    }
    setValue(
      `preferredAddresses.${index}.address`,
      place.formatted_address ?? ''
    );
    setValue(
      `preferredAddresses.${index}.addressLatitude`,
      place.geometry.location.lat()
    );
    setValue(
      `preferredAddresses.${index}.addressLongitude`,
      place.geometry.location.lng()
    );
  };

  const handleAddPreferredAddress = () => {
    appendPreferredAddress({
      address: '',
      addressLatitude: 0,
      addressLongitude: 0,
      maxDistanceMiles: DEFAULT_PREFERRED_DISTANCE,
    });
  };

  const handleDisableConfirm = () => {
    setValue('receiveChannels', []);
    setShowDisableModal(false);
  };

  const handleDisableCancel = () => {
    setShowDisableModal(false);
  };

  const handleJobTitlesChange = (
    newValue: string[],
    fieldOnChange: (value: string[]) => void
  ) => {
    if (newValue.length === 0) {
      showWarningNotification('At least one job title must be selected.', {
        title: 'Selection Required',
      });
      return;
    }
    fieldOnChange(newValue);
  };

  useEffect(() => {
    reset(initialValues);
  }, [existingPreferences, reset, initialValues]);

  const saveDefaults = useCallback(() => {
    handleInternalSubmit(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  useEffect(() => {
    if (onAutoSaveDefaults && isFirstTimeUser) {
      onAutoSaveDefaults(saveDefaults);
    }
  }, [onAutoSaveDefaults, isFirstTimeUser, saveDefaults]);

  return (
    <Form control={control} onSubmit={({ data }) => handleInternalSubmit(data)}>
      <Stack gap="lg">
        <Text c="dimmed">
          Personalize your new job alerts. ALL jobs can always be seen on Public
          Jobs section.
        </Text>

        {enableSuggestedWorkers && (
          <Controller
            control={control}
            name="allowSuggestedAppearance"
            render={({ field }) => (
              <Checkbox
                checked={field.value}
                onChange={(event) => {
                  if (isInNetwork) {
                    field.onChange(event.currentTarget.checked);
                  } else {
                    showWarningNotification(
                      'Join In-Network to activate this feature'
                    );
                  }
                }}
                label="In-Network Perk: automatically be a Suggested Worker"
                description={
                  <>
                    The moment a company creates a local home address job that
                    matches your filters below, you appear within their job,
                    with your contact info.
                    {!isInNetwork && (
                      <>
                        {' '}
                        <Anchor
                          component="button"
                          type="button"
                          onClick={handleGetInNetwork}
                          style={{
                            fontSize: 'inherit',
                            lineHeight: 'inherit',
                            fontWeight: '700',
                            textDecoration: 'underline',
                          }}
                        >
                          Join now
                        </Anchor>
                      </>
                    )}
                  </>
                }
                styles={{ label: { fontWeight: 700 } }}
              />
            )}
          />
        )}

        <Divider />

        {/* Choose how to get notifications for new jobs in your area */}
        <Grid align="center">
          <Grid.Col span={{ base: 12, sm: 8 }}>
            <Title order={5}>
              Choose how to get notifications for new jobs in your area
            </Title>
            <Text c="dimmed" size="sm">
              Remember, the faster you apply, you get in before others!
            </Text>
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 4 }}>
            <Stack gap={4}>
              <Controller
                control={control}
                name="frequency"
                render={({ field }) => (
                  <Select
                    label={undefined}
                    checkIconPosition="right"
                    allowDeselect={false}
                    data={[
                      {
                        value: NotificationFrequency.EVERY_4_HOURS,
                        label: isInNetwork
                          ? 'Every 4 hours'
                          : 'Every 4 hours (IN-NETWORK only)',
                        disabled: !isInNetwork,
                      },
                      { value: NotificationFrequency.DAILY, label: 'Daily' },
                      {
                        value: NotificationFrequency.PAUSED,
                        label: 'Pause for 30 days',
                      },
                    ]}
                    {...field}
                  />
                )}
              />
              {daysUntilResume && (
                <Text size="xs" c="dimmed">
                  Resumes in {daysUntilResume} day
                  {daysUntilResume !== 1 ? 's' : ''}
                </Text>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        {/* Choose how to receive these notifications */}
        <Stack gap="xs">
          <Title order={5}>Choose how to receive these notifications</Title>
          <Text c="dimmed" size="sm">
            Feel free to select both so you don&apos;t miss out.
          </Text>
          <Controller
            control={control}
            name="receiveChannels"
            render={({ field }) => (
              <Checkbox.Group
                {...field}
                onChange={(newValue) => {
                  // If trying to deselect all channels, show confirmation modal
                  if (newValue.length === 0) {
                    setShowDisableModal(true);
                  } else {
                    field.onChange(newValue);
                  }
                }}
              >
                <Group>
                  <Checkbox value={NotificationChannels.EMAIL} label="Email" />
                  <Checkbox
                    value={NotificationChannels.PUSH_NOTIFICATION}
                    label="App"
                  />
                </Group>
              </Checkbox.Group>
            )}
          />
        </Stack>

        <Divider />

        {/* Job preference filters intro */}
        <Stack gap={4}>
          <Title order={5}>Job preference filters</Title>
          <Text c="dimmed" size="sm">
            By configuring these filters, the notifications you receive for new
            jobs in your area will be pre-filtered based on your preferences.
          </Text>
        </Stack>

        {/* Job Position Titles */}
        <Stack gap="xs">
          <Title order={6}>Job Position Titles</Title>
          <Text c="dimmed" size="sm">
            Receive job alerts for these positions.
          </Text>
          <Controller
            control={control}
            name="jobTitles"
            render={({ field }) => {
              return (
                <Checkbox.Group
                  {...field}
                  onChange={(newValue) =>
                    handleJobTitlesChange(newValue, field.onChange)
                  }
                >
                  <Box
                    style={{
                      columns: '2',
                      columnGap: 'var(--mantine-spacing-xs)',
                    }}
                  >
                    {workerProfileAllowedOptions?.jobTitles.map(
                      (jobTitle: { id: number; name: string }) => (
                        <Box
                          key={jobTitle.id}
                          style={{
                            breakInside: 'avoid',
                            marginBottom: 'var(--mantine-spacing-xs)',
                          }}
                        >
                          <Checkbox
                            value={String(jobTitle.id)}
                            label={jobTitle.name}
                          />
                        </Box>
                      )
                    )}
                  </Box>
                </Checkbox.Group>
              );
            }}
          />
        </Stack>

        {/* Minimum Pay Rate */}
        <Stack gap="xs">
          <Title order={6}>Minimum Pay Rate</Title>
          <Text c="dimmed" size="sm">
            Choose your minimum preferred hourly/daily rate.
          </Text>

          <Grid align="center" gutter="xs">
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="xs">
                <Controller
                  control={control}
                  name="minHourlyEnabled"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      label="Minimum hourly rate"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="minHourlyRate"
                  render={({ field }) => (
                    <NumberInput
                      min={1}
                      max={100}
                      clampBehavior="strict"
                      placeholder="Enter amount"
                      leftSection={
                        <Text px="xs" fs="italic">
                          $
                        </Text>
                      }
                      hideControls
                      disabled={!minHourlyEnabled}
                      {...field}
                      value={field.value ?? undefined}
                    />
                  )}
                />
              </Stack>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="xs">
                <Controller
                  control={control}
                  name="minDailyEnabled"
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onChange={field.onChange}
                      label="Minimum daily rate"
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="minDailyRate"
                  render={({ field }) => (
                    <NumberInput
                      min={1}
                      max={1000}
                      clampBehavior="strict"
                      placeholder="Enter amount"
                      leftSection={
                        <Text px="xs" fs="italic">
                          $
                        </Text>
                      }
                      hideControls
                      disabled={!minDailyEnabled}
                      {...field}
                      value={field.value ?? undefined}
                    />
                  )}
                />
              </Stack>
            </Grid.Col>
          </Grid>
        </Stack>

        {/* Exclude Companies */}
        <Stack gap="xs">
          <Title order={6}>Exclude Companies</Title>
          <Text c="dimmed" size="sm">
            Search and select any companies you DO NOT want in My Jobs and New
            job alerts.
          </Text>
          <Controller
            control={control}
            name="excludeCompany"
            render={({ field }) => (
              <MultiSelect
                placeholder="Enter company name"
                searchable
                searchValue={companySearch}
                onSearchChange={handleCompanySearchChange}
                checkIconPosition="right"
                nothingFoundMessage={
                  companySearch.length < MIN_SEARCH_CHARS
                    ? `Type at least ${MIN_SEARCH_CHARS} characters to search`
                    : 'No companies found'
                }
                comboboxProps={{ position: 'top' }}
                {...field}
                data={excludeCompanyOptions}
              />
            )}
          />
        </Stack>

        {/* Location: profile (home) address, read-only here — edited in Profile */}
        <Stack gap="xs">
          <Title order={6}>Location</Title>
          <Text c="dimmed" size="sm">
            Receive new job notifications around your profile address. To change
            it, update your profile.
          </Text>
          <Group gap="xs">
            <FiMapPin />
            <Controller
              control={control}
              name="currentAddress"
              render={({ field }) => <Text size="sm">{field.value}</Text>}
            />
          </Group>
          <Controller
            control={control}
            name="distance"
            render={({ field }) => {
              const handleDistanceChange = (value: number) => {
                if (!isInNetwork && value > DEFAULT_PREFERRED_DISTANCE) {
                  field.onChange(DEFAULT_PREFERRED_DISTANCE);
                  return;
                }
                field.onChange(value);
              };

              return (
                <Stack gap={0}>
                  <Text size="sm" fw="bold">
                    {field.value} miles
                  </Text>
                  <Slider
                    min={MIN_DISTANCE_MILES}
                    max={MAX_DISTANCE_MILES}
                    step={1}
                    marks={
                      isInNetwork
                        ? undefined
                        : [
                            {
                              value: DEFAULT_PREFERRED_DISTANCE,
                              label: 'Max',
                            },
                          ]
                    }
                    {...field}
                    onChange={handleDistanceChange}
                  />
                  {!isInNetwork && (
                    <Text size="sm" c="dimmed" mt="lg">
                      Your radius is capped at {DEFAULT_PREFERRED_DISTANCE}{' '}
                      miles.{' '}
                      <Anchor
                        component="button"
                        type="button"
                        onClick={handleGetInNetwork}
                      >
                        Join In-Network
                      </Anchor>{' '}
                      to expand it up to {MAX_DISTANCE_MILES} miles.
                    </Text>
                  )}
                </Stack>
              );
            }}
          />
        </Stack>

        {/* Additional addresses: In-Network (premium) only, see MAX_PREFERRED_ADDRESSES */}
        <Stack gap="xs">
          <Group gap="xs">
            <Title order={6}>Additional addresses</Title>
            {!isInNetwork && (
              <Badge color="yellow" variant="light">
                IN-NETWORK only
              </Badge>
            )}
          </Group>
          <Text c="dimmed" size="sm">
            Add up to {MAX_PREFERRED_ADDRESSES} extra addresses to also receive
            job alerts around them, each with its own distance.
          </Text>

          {isInNetwork ? (
            <Stack gap="md">
              {preferredAddressFields.map((item, index) => (
                <Stack key={item.id} gap="xs">
                  <Group align="center" wrap="nowrap">
                    <Box style={{ flex: 1 }}>
                      <Controller
                        control={control}
                        name={`preferredAddresses.${index}.address`}
                        render={({ field }) => (
                          <MapsAutoCompleteV2
                            placeholder="Enter address"
                            value={field.value}
                            onChange={(e) => field.onChange(e.target.value)}
                            setSelectedPlace={(place) =>
                              handleSelectPreferredPlace(index, place)
                            }
                          />
                        )}
                      />
                    </Box>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label="Remove address"
                      onClick={() => removePreferredAddress(index)}
                    >
                      <FiTrash2 />
                    </ActionIcon>
                  </Group>
                  <Controller
                    control={control}
                    name={`preferredAddresses.${index}.maxDistanceMiles`}
                    render={({ field }) => (
                      <Stack gap={0}>
                        <Text size="sm" fw="bold">
                          {field.value} miles
                        </Text>
                        <Slider
                          min={MIN_DISTANCE_MILES}
                          max={MAX_DISTANCE_MILES}
                          step={1}
                          {...field}
                        />
                      </Stack>
                    )}
                  />
                </Stack>
              ))}

              <Button
                variant="light"
                leftSection={<FiPlus />}
                disabled={
                  preferredAddressFields.length >= MAX_PREFERRED_ADDRESSES
                }
                onClick={handleAddPreferredAddress}
                style={{ alignSelf: 'flex-start' }}
              >
                Add address ({preferredAddressFields.length}/
                {MAX_PREFERRED_ADDRESSES})
              </Button>
            </Stack>
          ) : (
            <Alert color="yellow" variant="light">
              Additional addresses are available for In-Network workers only.
            </Alert>
          )}
        </Stack>

        {/* Footer actions */}
        <Group justify="flex-end">
          <Button variant="light" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            tt="capitalize"
            disabled={isSubmitting}
            loading={savePreferencesMutation.isPending}
            type="submit"
          >
            {isFirstTimeUser ? 'Save preferences' : 'Save changes'}
          </Button>
        </Group>

        {/* Confirmation modal for disabling all notification channels */}
        <DisableNotificationsConfirmModal
          opened={showDisableModal}
          onClose={handleDisableCancel}
          onConfirm={handleDisableConfirm}
        />
      </Stack>
    </Form>
  );
}
